import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from '@headlessui/react'
import { Fragment, memo, useEffect, useRef, useState } from 'react'
import { CircleFlag } from 'react-circle-flags'

import StepsProgress from '@Components/StepsProgress'
import AmazonIcon from '@Icons/amazon-logo.svg?react'
import GoogleShoppingIcon from '@Icons/google-shopping-icon.svg?react'
import XIcon from '@Icons/x-close-filter.svg?react'
import { usePriceApiMatchStatusQuery } from '@Store/api/product'
import type { PriceAPIResponse } from '@Store/types/batch-import'
import Tooltip from '@Utils/tool-tip'

const INITIAL_POLLING_INTERVAL = 5000

function ChannelSummary({
  isClosable = false,
  importId,
  onClose = (): void => {},
}: {
  isClosable?: boolean
  importId: number
  onClose?: () => void
}): JSX.Element {
  const data = useRef<PriceAPIResponse>()

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [isClosed, setIsClosed] = useState<boolean>(false)

  const { currentData, isSuccess } = usePriceApiMatchStatusQuery(importId, {
    pollingInterval: INITIAL_POLLING_INTERVAL,
  })

  if (currentData) {
    data.current = currentData
  }

  const handleClose = (): void => {
    if (!isClosable) return
    setIsClosed(true)
  }

  useEffect(() => {
    if (!isClosed && isSuccess && currentData) {
      setIsOpen(true)
    }
  }, [isClosed, isSuccess, currentData])

  useEffect(() => {
    if (isClosed) {
      setIsOpen(false)
      onClose()
    }
  }, [isClosed])

  // to match with BE implementation
  const percentage = data.current?.percentage || 0
  const currentStep =
    percentage <= 35 ? 1 : percentage >= 100 ? 4 : percentage >= 50 ? 3 : 2

  return (
    <Transition appear show={isOpen === true} as={Fragment}>
      <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 flex items-center justify-center overflow-auto "
            style={{
              background: 'rgba(41, 41, 41, 0.7)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <DialogPanel className="relative border rounded-2xl shadow-2xl bg-white-100 w-4/5 lg:w-2/5">
              <div className="flex flex-col p-4 w-full">
                <h1 className="text-lg text-gray-700 font-semibold ml-2 flex justify-between">
                  Channel Summary
                  {isClosable && (
                    <button onClick={handleClose}>
                      <XIcon />
                    </button>
                  )}
                </h1>
                <div className="flex-1 flex flex-col items-center justify-center mt-6 w-full">
                  <StepsProgress
                    steps={[
                      'Adding Products',
                      'Processing Product',
                      'Finding Competitors',
                    ]}
                    currentStep={currentStep}
                    className="mt-0 w-full"
                  />
                  <div className="font-medium text-3xl text-gray-800">
                    {data.current?.percentage
                      ? data.current?.percentage?.toFixed(0)
                      : '0'}{' '}
                    %
                  </div>
                </div>
                <div className="flex-1 flex flex-col mt-2 text-gray-800 text-sm">
                  {data.current && (
                    <>
                      <div className="flex-1 flex justify-between p-3">
                        <span>Processed Products</span>
                        <span>
                          {data.current.processed_product_count} /{' '}
                          {data.current.total_product_count}
                        </span>
                      </div>
                      <div className="flex-1 flex justify-between p-3 bg-gray-100">
                        <span>Products with a proper matching field</span>
                        <span>
                          {data.current.total_valid_product_count} /{' '}
                          {data.current.processed_product_count}
                        </span>
                      </div>
                      {data.current.is_amazon_enabled && (
                        <>
                          {Object.keys(data.current.amazon_job_response).map(
                            (key) => (
                              <div className="flex-1 flex justify-between p-3">
                                <div className="flex flex-row items-center">
                                  <span>Matched Products</span>{' '}
                                  <Tooltip
                                    text={
                                      data.current?.amazon_job_response[key]
                                        ?.country_name
                                    }
                                  >
                                    <CircleFlag
                                      countryCode={key}
                                      className="h-5 ml-1"
                                    />
                                  </Tooltip>{' '}
                                  <AmazonIcon className="h-4 ml-1" />
                                </div>
                                <span>
                                  {
                                    data.current?.amazon_job_response[key]
                                      .matched_product_count
                                  }{' '}
                                  /{' '}
                                  {
                                    data.current?.amazon_job_response[key]
                                      .valid_product_count
                                  }
                                </span>
                              </div>
                            ),
                          )}
                        </>
                      )}
                      {data.current.is_google_shopping_enabled && (
                        <>
                          {Object.keys(data.current.google_job_response).map(
                            (key) => (
                              <div className="flex-1 flex justify-between p-3 bg-gray-100">
                                <div className="flex flex-row items-center">
                                  <span>Matched Products</span>{' '}
                                  <Tooltip
                                    text={
                                      data.current?.google_job_response[key]
                                        ?.country_name
                                    }
                                  >
                                    <CircleFlag
                                      countryCode={key}
                                      className="h-5 ml-1"
                                    />
                                  </Tooltip>{' '}
                                  <GoogleShoppingIcon className="h-4 w-5 m-1" />
                                </div>
                                <span>
                                  {
                                    data.current?.google_job_response[key]
                                      .matched_product_count
                                  }{' '}
                                  /{' '}
                                  {
                                    data.current?.google_job_response[key]
                                      .valid_product_count
                                  }
                                </span>
                              </div>
                            ),
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </DialogPanel>
          </div>
        </TransitionChild>
      </Dialog>
    </Transition>
  )
}

export default memo(
  ChannelSummary,
  (prevProps, nextProps) =>
    prevProps.importId === nextProps.importId &&
    prevProps.isClosable === nextProps.isClosable,
)
